import React from 'react'
import styled from 'styled-components'
import { Trans, useTranslation } from 'react-i18next'
import {
  Cross as CrossIcon,
  Skeleton,
  QuestionMarkOutlined as QuestionMarkOutlinedIcon,
  Tooltip
  // @ts-ignore
} from '@netbasequid/core'
// @ts-ignore
import { sys } from '@netbasequid/theme'

import { useSysTheme, useDisplayTheme } from 'hooks/useUiState'
import { SystemTheme } from 'hooks/useUiState/types'
import { Theme } from 'features/ui/types'
import { useAppSelector, useAppDispatch } from 'app/hooks'
import {
  selectUnselectedLabels,
  selectSelectedLabels,
  selectDashboardInfoStatus,
  selectLabelsEntities,
  selectIsFiltered
} from 'features/dashboardInfo/selectors'
import { addLabel, removeLabel, removeAllLabels } from 'features/dashboardInfo/dashboardInfoSlice'
import { AutomationId } from 'utils/automationUtil'
import { selectHasIntroductionToolTips } from 'features/ui/selectors'
import { heapTrack, HeapEvent, HeapEventPageValue } from 'utils/heapUtils'
import { ScrollbarStyle } from 'styles'

import { LabelIntroductionTooltip } from '../introductionTooltip'

const LabelListContainer = styled.div`
  display: flex;
  flex-direction: column;
`
const Title = styled.div<{ $theme: SystemTheme }>`
  font-size: ${({ $theme }) => $theme.typescale[16].size};
  line-height: ${({ $theme }) => $theme.typescale[16].lineHeight};
  font-weight: ${({ $theme }) => $theme.fontWeight.semibold};
  font-family: var(--large-font-family);
  color: ${({ $theme }) => $theme.text.primary};
  margin-bottom: 8px;
  flex-shrink: 0;
  padding-left: 24px;
`
const ListContainer = styled.div<{ $mode: Theme }>`
  flex-grow: 1;
  overflow: auto;
  padding-right: 16px;
  padding-left: 24px;
  ${ScrollbarStyle}
`

const Label = styled.div<{ $theme: SystemTheme }>`
  font-size: ${({ $theme }) => $theme.typescale[12].size};
  line-height: ${({ $theme }) => $theme.typescale[12].lineHeight};
  font-weight: ${({ $theme }) => $theme.fontWeight.medium};
  color: ${({ $theme }) => $theme.text.placeholder};
  display: flex;
  align-items: center;
`
const ActionLabel = styled.div<{ $theme: SystemTheme }>`
  font-size: ${({ $theme }) => $theme.typescale[12].size};
  line-height: ${({ $theme }) => $theme.typescale[12].lineHeight};
  font-weight: ${({ $theme }) => $theme.fontWeight.medium};
  color: ${({ $theme }) => $theme.interaction.primary.default};
  cursor: pointer;
  &:hover {
    color: ${({ $theme }) => $theme.interaction.primary.hover};
  }
  &:active {
    color: ${({ $theme }) => $theme.interaction.primary.pressed};
  }
`

const CustomPillContainer = styled.div<{
  $mode: Theme
  $theme: SystemTheme
}>`
  width: 100%;
  min-width: 140px;
  padding: 5px 16px;
  border-radius: 16px;
  background-color: ${({ $mode }) => ($mode === 'light' ? '#0F12140F' : '#FFFFFF14')};
  color: ${({ $theme }) => $theme.text.primary};
  box-sizing: border-box;
  font-size: ${({ $theme }) => $theme.typescale[14].size};
  line-height: ${({ $theme }) => $theme.typescale[14].lineHeight};
  font-weight: ${({ $theme }) => $theme.fontWeight.regular};
  cursor: pointer;

  display: flex;
  align-items: center;
  margin: 4px 0;

  & + & {
    margin: 8px 0;
  }
`

const CustomPillLabel = styled.div`
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`
const CustomPillIconContainer = styled.span`
  width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: -4px;
`

const CustomPill = ({
  children,
  onClick,
  onClose,
  amount,
  displayAmount
}: {
  children: React.ReactNode
  onClick?: () => void
  onClose?: () => void
  amount?: number
  displayAmount?: boolean
}) => {
  const mode = useDisplayTheme()
  const theme = useSysTheme()

  return (
    <CustomPillContainer
      data-testid={AutomationId.sidePannel.label}
      $mode={mode}
      $theme={theme}
      onClick={onClick}>
      <CustomPillLabel>{children}</CustomPillLabel>
      {onClose ? (
        <CustomPillIconContainer onClick={onClose}>
          <CrossIcon color={theme.text.placeholder} />
        </CustomPillIconContainer>
      ) : (
        displayAmount && <span>{amount}</span>
      )}
    </CustomPillContainer>
  )
}

const Divider = styled.div<{ $theme: SystemTheme }>`
  height: 1px;
  background-color: ${({ $theme }) => $theme.common.divider[100]};
  margin: 12px 0;
`

const LabelContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

const EmptyResultContainer = styled.div<{ $theme: SystemTheme }>`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $theme }) => $theme.text.placeholder};
  font-size: ${({ $theme }) => $theme.typescale[14].size};
  line-height: ${({ $theme }) => $theme.typescale[14].lineHeight};
  font-weight: ${({ $theme }) => $theme.fontWeight.regular};
`

const StyledSkeleton = styled(Skeleton)`
  height: 32px;
  width: 100%;
  min-width: 140px;
  border-radius: 16px;
  margin: 4px 0;
  & + & {
    margin: 8px 0;
  }
`

const Link = styled.a.attrs({ target: '_blank', rel: 'noopener noreferrer' })<{
  mode: Theme
}>`
  color: ${({ mode }) => sys[mode].interaction.primary.default};
  cursor: ${({ href }) => (href ? 'pointer' : 'default')};
  &:hover {
    color: ${({ mode }) => sys[mode].interaction.primary.hover};
  }
  &:active {
    color: ${({ mode }) => sys[mode].interaction.primary.pressed};
  }
`

const EmptyResult = () => {
  const theme = useSysTheme()
  const mode = useDisplayTheme() as Theme
  const { t } = useTranslation()
  const isFiltered = useAppSelector(selectIsFiltered)
  return (
    <EmptyResultContainer $theme={theme}>
      {isFiltered ? (
        t('noResultsFound')
      ) : (
        <span>
          <Trans
            i18nKey="noLabelsYetLearnMore"
            components={{
              CustomLink: (
                <Link
                  mode={mode}
                  href="https://help.quid.com/en/articles/9875603-using-labels-and-folders"
                />
              )
            }}
          />
        </span>
      )}
    </EmptyResultContainer>
  )
}

const LoadingSkeletons = () => {
  const mode = useDisplayTheme()
  return (
    <div>
      <StyledSkeleton mode={mode} variation="rectangle" animation="wave" />
      <StyledSkeleton mode={mode} variation="rectangle" animation="wave" />
      <StyledSkeleton mode={mode} variation="rectangle" animation="wave" />
      <StyledSkeleton mode={mode} variation="rectangle" animation="wave" />
    </div>
  )
}

const IconContainer = styled.div<{ $theme: SystemTheme }>`
  display: flex;
  align-items: center;
  &:hover {
    cursor: pointer;
    & > svg {
      color: ${({ $theme }) => $theme.text.weak};
    }
  }
`

const LabelList = ({
  page,
  displayAmount
}: {
  page?: HeapEventPageValue
  displayAmount?: boolean
}) => {
  const theme = useSysTheme()
  const mode = useDisplayTheme()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const { hasLabelTooltip } = useAppSelector(selectHasIntroductionToolTips)
  const unselectedLabels = useAppSelector(selectUnselectedLabels)
  const selectedLabels = useAppSelector(selectSelectedLabels)
  const labelsEntities = useAppSelector(selectLabelsEntities)

  const hasSelectedLabels = selectedLabels.length > 0
  const hasUnselectedLabels = unselectedLabels.length > 0
  const hasLabels = hasSelectedLabels || hasUnselectedLabels
  const status = useAppSelector(selectDashboardInfoStatus)

  const handleLabelSelect = (label: string) => {
    dispatch(addLabel(label))
    heapTrack(HeapEvent.V4DashboardLabelSelected, { label, page })
  }

  return (
    <LabelListContainer>
      <Title data-testid={AutomationId.sidePannel.labelTitle} $theme={theme}>
        {t('label_other')}
      </Title>
      {hasLabelTooltip && (
        <div style={{ position: 'relative' }}>
          <LabelIntroductionTooltip />
        </div>
      )}
      <ListContainer $mode={mode}>
        {/* eslint-disable-next-line no-nested-ternary */}
        {status === 'loading' ? (
          <LoadingSkeletons />
        ) : hasLabels ? (
          <div>
            {hasSelectedLabels && (
              <>
                <LabelContainer>
                  <Label data-testid={AutomationId.sidePannel.labelSubtitle} $theme={theme}>
                    {t('selected')}
                  </Label>
                  <ActionLabel $theme={theme} onClick={() => dispatch(removeAllLabels())}>
                    {t('clear')}
                  </ActionLabel>
                </LabelContainer>
                {selectedLabels.map((label) => (
                  <CustomPill key={label} onClose={() => dispatch(removeLabel(label))}>
                    {label}
                  </CustomPill>
                ))}
              </>
            )}
            {hasSelectedLabels && hasUnselectedLabels && (
              <>
                <Divider $theme={theme} />
                <Label data-testid={AutomationId.sidePannel.labelSubtitle} $theme={theme}>
                  {t('additional')} &nbsp;
                  <Tooltip
                    mode={mode}
                    content={<span>{t('additionalTooltip')}</span>}
                    followCursor={false}
                    offset={[-65, 4]}
                    placement="top-end">
                    <IconContainer $theme={theme}>
                      <QuestionMarkOutlinedIcon fontSize={14} />
                    </IconContainer>
                  </Tooltip>
                </Label>
              </>
            )}
            {unselectedLabels.map((label) => (
              <CustomPill
                key={label}
                onClick={() => handleLabelSelect(label)}
                amount={labelsEntities[label].length}
                displayAmount={displayAmount}>
                {label}
              </CustomPill>
            ))}
          </div>
        ) : (
          <EmptyResult />
        )}
      </ListContainer>
    </LabelListContainer>
  )
}

export default LabelList
